import { useEffect, useMemo, useState } from 'react';
import { listCategories, openRequests, api } from '@/api';
import { MapPin, RefreshCw, Phone, Eye, MessageCircle, Users, Lock, ArrowLeft, Filter, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate, Link } from 'react-router-dom';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';
const MAX_CONTACTS = 4;

const DISTRETTI_TICINO = [
  "Bellinzona", "Blenio", "Leventina", "Locarno",
  "Lugano", "Mendrisio", "Riviera", "Vallemaggia"
];

function CategoryBadge({name}){
  const colors = { Idraulico: '#5B8CFF', Muratore: '#22C55E', Elettricista: '#F97316' };
  const bg = colors[name] || '#5848FF';
  return <span className="px-3 h-7 inline-flex items-center rounded-full text-[12px] font-semibold text-white" style={{backgroundColor:bg}}>{name}</span>;
}

function sinceText(iso){
  try{
    const d = new Date(iso), now = new Date();
    const diffD = Math.max(0, Math.floor((now - d)/(1000*60*60*24)));
    if(diffD===0) return 'Oggi';
    if(diffD===1) return '1 giorno fa';
    if(diffD<7) return diffD + ' giorni fa';
    return Math.floor(diffD/7) + ' settimane fa';
  }catch{return ''}
}

export default function CompanyRequests(){
  const { t } = useLanguage();
  const nav = useNavigate();
  const [reqs,setReqs]=useState([]);
  const [cats,setCats]=useState([]);
  const [region,setRegion]=useState('');
  const [capFilter,setCapFilter]=useState('');
  const [category,setCategory]=useState('');
  const [detail,setDetail]=useState(null);
  const [loading, setLoading] = useState(true);
  const [contactStatus, setContactStatus] = useState({});

  const catsById = useMemo(()=> Object.fromEntries(cats.map(c=>[c.id,c.name])), [cats]);

  const refresh = (opts={}) => {
    setLoading(true);
    return openRequests(opts).then(r=>{ 
      const sorted = (r.data||[]).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReqs(sorted);
      setLoading(false);
      sorted.forEach(req => checkContactStatus(req.id));
    }).catch(() => setLoading(false));
  };

  const checkContactStatus = async (requestId) => {
    try {
      const res = await api.get('/requests/' + requestId + '/can-contact');
      setContactStatus(prev => ({ ...prev, [requestId]: res.data }));
    } catch (err) {}
  };

  const handleContact = async (request, contactType) => {
    try {
      const res = await api.post('/requests/' + request.id + '/contact', { requestId: request.id, contactType });
      const { contactValue, already_contacted } = res.data;
      if (!contactValue) { toast.error('Recapito non disponibile'); return; }
      
      if (contactType === 'whatsapp') {
        const phone = contactValue.replace(/\s/g, '').replace(/^\+/, '');
        const message = encodeURIComponent('Ciao, ti contatto da ServiceGo per la tua richiesta: ' + (request.title || 'Richiesta'));
        window.open('https://wa.me/' + phone + '?text=' + message, '_blank');
      } else if (contactType === 'call') {
        window.open('tel:' + contactValue, '_blank');
      } else if (contactType === 'email') {
        window.open('mailto:' + contactValue + '?subject=' + encodeURIComponent('ServiceGo - ' + (request.title || 'Richiesta')), '_blank');
      }
      if (!already_contacted) toast.success('Contatto registrato!');
      checkContactStatus(request.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  useEffect(()=>{ 
    listCategories().then(r=>setCats(r.data||[])); 
    refresh();
  }, []);

  const applyFilters = () => refresh({ city: region && region !== 'all' ? region : undefined, categoryId: category && category !== 'all' ? category : undefined, cap: capFilter.trim() || undefined });
  const clearFilters = () => { setRegion(''); setCategory(''); setCapFilter(''); refresh({}); };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => nav('/ditta/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
              <Link to="/"><img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" /></Link>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <Button variant="outline" onClick={() => refresh()} disabled={loading} className="rounded-xl">
                <RefreshCw className={'w-4 h-4 ' + (loading ? 'animate-spin' : '')} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Richieste Disponibili</h1>
          <p className="text-slate-500 mt-1">{reqs.length} richieste</p>
        </div>

        <div className="bg-white rounded-2xl p-4 sm:p-6 mb-6 border border-slate-200 shadow-sm">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="h-11 rounded-xl bg-slate-50"><MapPin className="w-4 h-4 mr-2" /><SelectValue placeholder="Distretto" /></SelectTrigger>
              <SelectContent className="bg-white rounded-xl max-h-60">
                <SelectItem value="all">{t('ui.allFilter2')}</SelectItem>
                {DISTRETTI_TICINO.map(d=> <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-11 rounded-xl bg-slate-50"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent className="bg-white rounded-xl max-h-60">
                <SelectItem value="all">Tutte</SelectItem>
                {cats.map(c=> <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input type="text" placeholder="Filtra per CAP" value={capFilter} onChange={e => setCapFilter(e.target.value)} className="h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" data-testid="cap-filter" />
            <Button className="h-11 rounded-xl bg-[#5848FF] text-white" onClick={applyFilters}><Search className="w-4 h-4 mr-2" />Applica</Button>
            {(region || category) && <Button variant="outline" className="h-11 rounded-xl" onClick={clearFilters}>Cancella</Button>}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" /></div>
        ) : reqs.length === 0 ? (
          <div className="bg-white rounded-3xl border p-12 text-center">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessuna richiesta disponibile</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reqs.map(req => {
              const catName = catsById[req.categoryId] || '—';
              const status = contactStatus[req.id] || {};
              const contactsUsed = MAX_CONTACTS - (status.contactsRemaining || MAX_CONTACTS);
              const canContact = status.canContact !== false;
              const alreadyContacted = status.alreadyContacted;
              const pref = status.contactPreference || req.contactPreference || 'whatsapp';
              
              return (
                <div key={req.id} className="bg-white rounded-2xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  <div className="p-5">
                    <div className="flex justify-between mb-3">
                      <CategoryBadge name={catName} />
                      <span className="text-[11px] text-slate-400">{sinceText(req.createdAt)}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2 line-clamp-1">{req.title || 'Richiesta'}</h3>
                    <p className="text-[13px] text-slate-600 mb-3 line-clamp-2">{req.description}</p>
                    <div className="flex items-center gap-2 text-[12px] text-slate-500 mb-4">
                      <MapPin className="w-3.5 h-3.5" /><span>{req?.address?.city || '—'}</span>
                    </div>
                    <div className={'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium ' + 
                      (contactsUsed >= MAX_CONTACTS && !alreadyContacted ? 'bg-red-100 text-red-600' : alreadyContacted ? 'bg-green-100 text-green-600' : 'bg-indigo-100 text-indigo-600')}>
                      <Users className="w-3.5 h-3.5" />
                      {alreadyContacted ? 'Già contattato' : contactsUsed + '/' + MAX_CONTACTS}
                    </div>
                  </div>
                  <div className="px-5 py-3 bg-slate-50 border-t flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 rounded-lg" onClick={() => { api.post('/requests/' + req.id + '/view'); setDetail(req); }}>
                      <Eye className="w-4 h-4 mr-1" />Dettagli
                    </Button>
                    {canContact ? (
                      pref === 'whatsapp' ? (
                        <Button size="sm" className="flex-1 rounded-lg bg-green-500 hover:bg-green-600 text-white" onClick={() => handleContact(req, 'whatsapp')}>
                          <MessageCircle className="w-4 h-4 mr-1.5" />WhatsApp
                        </Button>
                      ) : pref === 'telefono' ? (
                        <Button size="sm" className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white" onClick={() => handleContact(req, 'call')}>
                          <Phone className="w-4 h-4 mr-1.5" />Chiama
                        </Button>
                      ) : (
                        <Button size="sm" className="flex-1 rounded-lg bg-purple-500 hover:bg-purple-600 text-white" onClick={() => handleContact(req, 'email')}>
                          <MessageCircle className="w-4 h-4 mr-1.5" />Email
                        </Button>
                      )
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-[11px] text-slate-400 bg-slate-100 rounded-lg">
                        <Lock className="w-3 h-3 mr-1" />Limite
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="rounded-2xl bg-white max-w-lg">
          <DialogHeader><DialogTitle>{t('ui.requestDetail')}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <CategoryBadge name={catsById[detail.categoryId] || '—'} />
              <h3 className="text-lg font-semibold">{detail.title || 'Richiesta'}</h3>
              <p className="text-slate-600">{detail.description}</p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="w-4 h-4" />
                {detail?.address?.street} {detail?.address?.number}, {detail?.address?.cap} {detail?.address?.city}
              </div>
              {detail.urgent && <span className="inline-block px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm">⚠️ Urgente</span>}
              <div className="pt-4 border-t flex gap-3">
                {(() => {
                  const pref = detail.contactPreference || 'whatsapp';
                  if (pref === 'whatsapp') return (
                    <Button className="flex-1 rounded-xl bg-green-500 hover:bg-green-600 text-white" onClick={() => { handleContact(detail, 'whatsapp'); setDetail(null); }}>
                      <MessageCircle className="w-4 h-4 mr-2" />Contatta su WhatsApp
                    </Button>
                  );
                  if (pref === 'telefono') return (
                    <Button className="flex-1 rounded-xl bg-blue-500 hover:bg-blue-600 text-white" onClick={() => { handleContact(detail, 'call'); setDetail(null); }}>
                      <Phone className="w-4 h-4 mr-2" />Chiama ora
                    </Button>
                  );
                  return (
                    <Button className="flex-1 rounded-xl bg-purple-500 hover:bg-purple-600 text-white" onClick={() => { handleContact(detail, 'email'); setDetail(null); }}>
                      <MessageCircle className="w-4 h-4 mr-2" />Invia Email
                    </Button>
                  );
                })()}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
